"use client";

import { useState, useEffect } from "react";
import { CommentSection } from "./comment-section";
import { LikeButton } from "./like-button";
import { ViewCount } from "./view-count";
import { ShareButtons } from "./share-buttons";
import { useBlogInteractions } from "@/hooks/use-blog-interactions";
import { Button } from "@/components/ui/button";
import { ErrorBoundary } from "react-error-boundary";
import { v4 as uuidv4 } from 'uuid';

// Error fallback component for blog interactions
function InteractionsErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="p-4 border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 rounded-md text-red-800 dark:text-red-200">
      <p className="font-medium">Something went wrong loading blog interactions</p>
      <p className="text-sm mt-2">{error.message}</p>
      <button 
        onClick={resetErrorBoundary}
        className="mt-4 text-sm px-4 py-2 bg-red-100 dark:bg-red-800/30 hover:bg-red-200 dark:hover:bg-red-800/50 rounded-md transition-colors"
      >
        Try again
      </button>
    </div>
  );
}

interface BlogPostInteractionsProps {
  slug: string;
}

export function BlogPostInteractions({ slug }: BlogPostInteractionsProps) {
  const {
    viewCount,
    likeCount,
    liked,
    comments,
    isLoading,
    error,
    toggleLike,
    addComment,
    refreshComments
  } = useBlogInteractions(slug);

  // Load comments only once when component mounts
  useEffect(() => {
    if (slug) {
      // Only attempt to load comments once on initial render
      const loadCommentsOnce = async () => {
        try {
          await refreshComments();
        } catch (err) {
          // If there's an error, will handle through the error state
          console.error("Initial comments load failed", err);
          // Switch to local comments mode automatically
          setUseLocalComments(true);
        }
      };
      
      loadCommentsOnce();
    }
    // Intentionally exclude refreshComments from dependencies to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Local state for client-side fallback
  const [localComments, setLocalComments] = useState<Array<any>>([]);
  const [useLocalComments, setUseLocalComments] = useState(false);

  // If server comments fail to load, switch to local comments after 3 seconds
  useEffect(() => {
    if (error.comments) {
      const timer = setTimeout(() => {
        setUseLocalComments(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error.comments]);

  // Custom comment handler to ensure successful comments are shown immediately
  const handleAddComment = async (commentData: { name: string; email?: string; content: string; parentId?: string | null }) => {
    try {
      // Try to use the real API first
      await addComment(commentData);
      refreshComments();
    } catch (err) {
      console.error("Falling back to client-side comments", err);
      // Fallback to client-side comments if API fails
      setUseLocalComments(true);
      
      const newComment = {
        id: uuidv4(),
        user_name: commentData.name,
        content: commentData.content,
        created_at: new Date().toISOString(),
        replies: []
      };
      
      // If it's a reply, add it to the parent comment
      if (commentData.parentId) {
        setLocalComments(prevComments => {
          return prevComments.map(comment => {
            if (comment.id === commentData.parentId) {
              return {
                ...comment,
                replies: [...(comment.replies || []), newComment]
              };
            }
            return comment;
          });
        });
      } else {
        // Otherwise add it as a new top-level comment
        setLocalComments(prevComments => [...prevComments, newComment]);
      }
    }
  };

  // Only show error message for likes issues or if not using local comments
  if ((error.likes) || (error.comments && !useLocalComments)) {
    const errorMessage = error.likes || error.comments || "An error occurred";
    return <InteractionsErrorFallback error={new Error(errorMessage)} resetErrorBoundary={() => window.location.reload()} />;
  }

  // Show loading state only if not using local comments
  if (isLoading.comments && !useLocalComments && !error.comments) {
    return <div className="animate-pulse space-y-4">
      <div className="h-10 bg-muted rounded-md w-1/4"></div>
      <div className="h-24 bg-muted rounded-md"></div>
      <div className="h-16 bg-muted rounded-md w-3/4"></div>
    </div>;
  }

  return (
    <ErrorBoundary FallbackComponent={InteractionsErrorFallback}>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <LikeButton count={likeCount} liked={liked} onToggleLike={toggleLike} />
            <ViewCount count={viewCount} />
          </div>
          
          <ShareButtons url={`/blog/${slug}`} />
        </div>

        {useLocalComments && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3 mb-4">
            <p className="text-amber-800 dark:text-amber-300 text-sm">
              Using offline comment mode - comments will not be saved to the server and will be lost on page refresh.
            </p>
          </div>
        )}
        
        <CommentSection 
          comments={useLocalComments ? localComments : comments} 
          onAddComment={handleAddComment} 
          className="mt-6" 
        />
      </div>
    </ErrorBoundary>
  );
}
